import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import axios from '../services/axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmail: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  
  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');
        
        if (!token) {
          throw new Error('Verification token is missing');
        }
        
        const response = await axios.get(`/api/auth/verify-email/${token}`);
        
        if (response.data.token) {
          // Set the newly provided token as a cookie
          document.cookie = `token=${response.data.token}; path=/; max-age=${24 * 60 * 60}`;
          
          // If we have user info, update the auth context
          if (response.data.user) {
            updateUser(response.data.user);
          }
        }
        
        setSuccess(true);
        setLoading(false);
        
        // Redirect after successful verification
        setTimeout(() => {
          navigate(response.data.redirectTo || '/settings');
        }, 3000);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Email verification failed');
        setLoading(false);
      }
    };
    
    verifyEmail();
  }, [location.search, navigate, updateUser]);
  
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="sm">
          <Box sx={{ mt: 8, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Email Verification
            </Typography>
            
            <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Your email has been verified successfully!
                  </Alert>
                  <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                    You will be redirected to your account settings in a moment.
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button 
                      variant="contained" 
                      onClick={() => navigate('/settings')}
                    >
                      Go to Settings
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default VerifyEmail; 